import type { Message } from '@/api/types';
import { giftEmoji, giftName } from '@/features/gifts/model';

export type MsgStatus = 'sending' | 'sent' | 'failed';
export interface ChatMessage extends Message {
  status: MsgStatus;
  /** Inline reason when status = failed. */
  error?: string;
}

export const MAX_BODY = 1000;

/** One-line text for lists, toasts and notifications (challenges have no body of their own). */
export function messagePreview(m: Pick<Message, 'body' | 'kind' | 'challenge' | 'deleted' | 'gift'>): string {
  if (m.deleted) return m.body;
  // v8: a gift message's body is the gift id.
  if (m.kind === 'gift') {
    const id = m.gift?.gift ?? m.body;
    return `${giftEmoji(id)} ${m.gift?.name || giftName(id)}`;
  }
  if (m.kind === 'challenge' || m.challenge) {
    if (m.body) return m.body; // server label, e.g. "🎨 Doodle challenge"
    const t = m.challenge?.type;
    return t === 'doodle' ? '🎨 Sent a doodle challenge' : t === 'guess_answer' ? '🤔 Sent a Guess My Answer challenge' : m.body || '🎯 Sent a challenge';
  }
  return m.body;
}
export const UNSEND_WINDOW_MS = 10 * 60_000;
export const MAX_REPORT_MESSAGES = 20;

export const fromServer = (m: Message): ChatMessage => ({ ...m, body: m.deleted ? '' : m.body, status: 'sent' });

function sameMessage(a: ChatMessage, b: ChatMessage): boolean {
  if (a.id > 0 && b.id > 0 && a.id === b.id) return true;
  // client_id is unique per sender (SPEC_V2 §6): that is how an optimistic
  // message is reconciled with the HTTP response *and* the WS echo.
  return !!a.client_id && a.client_id === b.client_id && a.sender_id === b.sender_id;
}

function order(a: ChatMessage, b: ChatMessage): number {
  const ap = a.id <= 0;
  const bp = b.id <= 0;
  if (ap !== bp) return ap ? 1 : -1; // confirmed messages first, then pending
  if (!ap) return a.id - b.id;
  return Date.parse(a.created_at) - Date.parse(b.created_at);
}

/**
 * Merge messages into an ascending list. Deduplicates by server id and by
 * (sender, client_id); a confirmed message always replaces a pending/failed
 * copy, never the other way round.
 */
export function upsertMessages(list: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  let out = list.slice();
  for (const m of incoming) {
    const i = out.findIndex((x) => sameMessage(x, m));
    if (i === -1) {
      out.push(m);
      continue;
    }
    const prev = out[i]!;
    if (prev.status === 'sent' && m.status !== 'sent') continue; // stale optimistic copy
    // A deletion wins over an older non-deleted copy of the same message.
    out[i] = prev.deleted && !m.deleted && m.status === 'sent' && prev.status === 'sent' ? prev : { ...prev, ...m };
  }
  out = out.sort(order);
  return out;
}

export function oldestId(list: ChatMessage[]): number | undefined {
  const first = list.find((m) => m.id > 0);
  return first?.id;
}

export function newestConfirmed(list: ChatMessage[]): ChatMessage | undefined {
  for (let i = list.length - 1; i >= 0; i--) if (list[i]!.id > 0) return list[i];
  return undefined;
}

export function canUnsend(m: ChatMessage, myId: string, now = Date.now()): boolean {
  if (m.kind === 'challenge' || m.challenge) return false; // challenges can't be unsent (SPEC_V3 §9)
  if (m.kind === 'gift') return false; // nor gifts (SPEC_V8 §3)
  return m.sender_id === myId && m.status === 'sent' && !m.deleted && now - Date.parse(m.created_at) < UNSEND_WINDOW_MS;
}

/** Id of my newest message the friend has read (for the "Seen" marker). */
export function seenMarker(list: ChatMessage[], myId: string, peerReadUpTo: number): number | null {
  let seen: number | null = null;
  for (const m of list) if (m.sender_id === myId && m.id > 0 && m.id <= peerReadUpTo && !m.deleted) seen = m.id;
  return seen;
}

/** Same-day grouping labels for the thread. */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

export function validateBody(raw: string): string | null {
  const body = raw.trim();
  if (!body) return 'Write something first.';
  if (body.length > MAX_BODY) return `Keep it under ${MAX_BODY} characters.`;
  return null;
}
